import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Edit, X } from "lucide-react";
import { toast } from "sonner";

const empty = { title: "", awarded_by: "", year: 2024, description: "", type: "Award" };
const TYPES = ["Award", "Affiliation", "Publication", "Recognition"];

export default function AwardsAdmin() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/awards").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, year: parseInt(form.year) || null };
    try {
      if (editId) await api.put(`/admin/awards/${editId}`, payload);
      else await api.post("/admin/awards", payload);
      toast.success("Saved"); setForm(empty); setEditId(null); load();
    } catch (err) { toast.error("Failed"); }
  };
  const edit = (r) => { setForm({ ...r, year: r.year || "" }); setEditId(r.id); };
  const remove = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/admin/awards/${id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div data-testid="admin-awards" className="space-y-8">
      <header><div className="label-eyebrow mb-2">Manage</div>
        <h1 className="font-display text-4xl text-[hsl(var(--primary))]">Awards & Affiliations</h1></header>

      <form onSubmit={save} className="bg-white border border-[hsl(var(--border-c))] p-6 grid sm:grid-cols-2 gap-4">
        <div><Label>Title *</Label><Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="aw-title" /></div>
        <div><Label>Type</Label>
          <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
            <SelectTrigger data-testid="aw-type"><SelectValue /></SelectTrigger>
            <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Awarded by</Label><Input value={form.awarded_by} onChange={(e) => setForm({ ...form, awarded_by: e.target.value })} /></div>
        <div><Label>Year</Label><Input type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} /></div>
        <div className="sm:col-span-2"><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
        <div className="sm:col-span-2 flex gap-3">
          <Button type="submit" data-testid="aw-save" className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary-hover))] rounded-none uppercase tracking-widest">
            {editId ? <Edit size={14} className="mr-2" /> : <Plus size={14} className="mr-2" />}{editId ? "Update" : "Add"}
          </Button>
          {editId && <Button type="button" variant="outline" onClick={() => { setForm(empty); setEditId(null); }} className="rounded-none"><X size={14} className="mr-2" />Cancel</Button>}
        </div>
      </form>

      <div className="bg-white border border-[hsl(var(--border-c))]">
        <table className="w-full text-sm">
          <thead className="bg-[hsl(var(--bg-muted))]"><tr>
            {["Title", "Type", "By", "Year", ""].map((h) => <th key={h} className="text-left p-3 font-medium uppercase text-[10px] tracking-widest">{h}</th>)}
          </tr></thead>
          <tbody>
            {list.map((r) => (
              <tr key={r.id} className="border-t border-[hsl(var(--border-c))]">
                <td className="p-3 font-medium">{r.title}</td>
                <td className="p-3">{r.type}</td>
                <td className="p-3">{r.awarded_by}</td>
                <td className="p-3">{r.year}</td>
                <td className="p-3 flex gap-3">
                  <button onClick={() => edit(r)}><Edit size={14} /></button>
                  <button onClick={() => remove(r.id)} className="text-[hsl(var(--accent))]"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
